import { useEffect, useId, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

type CountryOption = {
  name: string
  code: string
  dialCode: string
  flag: string
}

const COUNTRY_OPTIONS: CountryOption[] = [
  { name: 'Nigeria', code: 'NG', dialCode: '+234', flag: '🇳🇬' },
  { name: 'Ghana', code: 'GH', dialCode: '+233', flag: '🇬🇭' },
  { name: 'Kenya', code: 'KE', dialCode: '+254', flag: '🇰🇪' },
  { name: 'South Africa', code: 'ZA', dialCode: '+27', flag: '🇿🇦' },
  { name: 'United States', code: 'US', dialCode: '+1', flag: '🇺🇸' },
  { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧' },
  { name: 'France', code: 'FR', dialCode: '+33', flag: '🇫🇷' },
  { name: 'Germany', code: 'DE', dialCode: '+49', flag: '🇩🇪' },
  { name: 'Spain', code: 'ES', dialCode: '+34', flag: '🇪🇸' },
  { name: 'Italy', code: 'IT', dialCode: '+39', flag: '🇮🇹' },
  { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳' },
  { name: 'Pakistan', code: 'PK', dialCode: '+92', flag: '🇵🇰' },
  { name: 'Bangladesh', code: 'BD', dialCode: '+880', flag: '🇧🇩' },
  { name: 'United Arab Emirates', code: 'AE', dialCode: '+971', flag: '🇦🇪' },
  { name: 'Saudi Arabia', code: 'SA', dialCode: '+966', flag: '🇸🇦' },
  { name: 'Australia', code: 'AU', dialCode: '+61', flag: '🇦🇺' },
  { name: 'New Zealand', code: 'NZ', dialCode: '+64', flag: '🇳🇿' },
  { name: 'Brazil', code: 'BR', dialCode: '+55', flag: '🇧🇷' },
  { name: 'Mexico', code: 'MX', dialCode: '+52', flag: '🇲🇽' },
  { name: 'Egypt', code: 'EG', dialCode: '+20', flag: '🇪🇬' },
]

const DEFAULT_COUNTRY = 'NG'

const normalizeDigits = (value: string) => value.replace(/\D/g, '')

const normalizeE164 = (value: string) => {
  const digits = normalizeDigits(value)
  return digits ? `+${digits}` : ''
}

interface PhoneNumberInputProps {
  value: string
  onChange: (value: string) => void
  id?: string
  name?: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  defaultCountry?: string
  containerClassName?: string
  selectClassName?: string
  inputClassName?: string
  leadingIcon?: ReactNode
}

export function PhoneNumberInput({
  value,
  onChange,
  id,
  name,
  required,
  disabled,
  placeholder = 'Phone number',
  defaultCountry = DEFAULT_COUNTRY,
  containerClassName = '',
  selectClassName = '',
  inputClassName = '',
  leadingIcon,
}: PhoneNumberInputProps) {
  const inputId = useId()
  const resolvedId = id || inputId

  const dialCodeIndex = useMemo(() => {
    return [...COUNTRY_OPTIONS].sort(
      (a, b) => b.dialCode.length - a.dialCode.length
    )
  }, [])

  const fallbackOption =
    COUNTRY_OPTIONS[0] || {
      name: 'Default',
      code: defaultCountry,
      dialCode: '+',
      flag: '🌍',
    }

  const defaultOption =
    COUNTRY_OPTIONS.find((option) => option.code === defaultCountry) ||
    fallbackOption

  const [selectedCode, setSelectedCode] = useState(defaultOption.code)
  const [localNumber, setLocalNumber] = useState('')

  const selectedOption =
    COUNTRY_OPTIONS.find((option) => option.code === selectedCode) ||
    defaultOption

  useEffect(() => {
    if (!value) {
      setLocalNumber('')
      return
    }

    const normalized = normalizeE164(value)
    const match = dialCodeIndex.find((option) =>
      normalized.startsWith(option.dialCode)
    )
    if (match) {
      setSelectedCode(match.code)
      setLocalNumber(normalized.slice(match.dialCode.length))
      return
    }

    setLocalNumber(normalizeDigits(value))
  }, [value, dialCodeIndex])

  const emitValue = (dialCode: string, raw: string) => {
    const digits = normalizeDigits(raw)
    onChange(digits ? `${dialCode}${digits}` : '')
  }

  const handleSelectChange = (code: string) => {
    const next =
      COUNTRY_OPTIONS.find((option) => option.code === code) || defaultOption
    setSelectedCode(next.code)
    emitValue(next.dialCode, localNumber)
  }

  const handleNumberChange = (raw: string) => {
    if (raw.trim().startsWith('+')) {
      const normalized = normalizeE164(raw)
      const match = dialCodeIndex.find((option) =>
        normalized.startsWith(option.dialCode)
      )
      if (match) {
        setSelectedCode(match.code)
        const rest = normalized.slice(match.dialCode.length)
        setLocalNumber(rest)
        emitValue(match.dialCode, rest)
        return
      }
    }

    setLocalNumber(raw)
    emitValue(selectedOption.dialCode, raw)
  }

  return (
    <div
      className={`flex w-full items-center rounded-lg border border-gray-300 ${containerClassName}`}
    >
      {/* Country code selector — compact */}
      <div className='relative shrink-0 flex items-center'>
        <select
          value={selectedCode}
          onChange={(e) => handleSelectChange(e.target.value)}
          disabled={disabled}
          className={`absolute inset-0 w-full opacity-0 cursor-pointer ${selectClassName}`}
          aria-label='Country code'
        >
          {COUNTRY_OPTIONS.map((option) => (
            <option key={option.code} value={option.code}>
              {option.flag} {option.name} ({option.dialCode})
            </option>
          ))}
        </select>
        {/* Visible compact display */}
        <div className='pointer-events-none flex items-center gap-1 pl-3 pr-2 py-2.5'>
          <span className='text-sm text-gray-700 whitespace-nowrap'>
            {selectedOption.flag} {selectedOption.dialCode}
          </span>
          <svg className='h-3 w-3 text-gray-400 shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </div>
      </div>

      {/* Divider */}
      <div className='h-6 w-px bg-gray-300 shrink-0' />

      {/* Phone input — takes remaining space */}
      <div className='relative flex-1 min-w-0'>
        {leadingIcon && (
          <span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'>
            {leadingIcon}
          </span>
        )}
        <input
          type='tel'
          id={resolvedId}
          name={name}
          value={localNumber}
          onChange={(e) => handleNumberChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          inputMode='tel'
          className={`w-full bg-transparent px-3 py-2.5 text-sm text-gray-900 outline-none placeholder:text-gray-400 ${
            leadingIcon ? 'pl-9' : ''
          } ${inputClassName}`}
        />
      </div>
    </div>
  )
}
